import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'

import { map } from 'rxjs'

import { ChatMessage, MessageContent } from '@yoshu-lib/models/chat-message.model'
import { Chat } from '@yoshu-lib/models/chat.model'
import { PaginatedResponse, PaginationRequest } from '@yoshu-lib/models/paginated-response.model'

import { ENVIRONMENT } from '@yoshu-lib/core/tokens/environment.token'

import { cleanHttpParams } from '@yoshu-lib/utils/clean-http-params'
import { isJSONString } from '@yoshu-lib/utils/is-json-string'

import { ChatSummaryResponse } from './chat-summary-response.model'
import { AddNewParticipantBody, CreateNewChatBody } from './create-chat-request.model'
import { CreateNewMessageBody } from './create-message-request.model'
import { ChatUpdateBody } from './update-chat-request.model'
import { MessageUpdateBody } from './update-message-request.model'

@Injectable({ providedIn: 'root' })
export class ChatsService {
  private env = inject(ENVIRONMENT)
  private http = inject(HttpClient)

  createChat(body: CreateNewChatBody, token?: string) {
    body.tenant = this.env.tenantId
    return this.http.post<Chat>(`${this.env.apiUrl}/chats/`, body, {
      params: cleanHttpParams({ token })
    })
  }

  getChats(params?: PaginationRequest & { token?: string }) {
    const { token, page, size } = params ?? {}

    return this.http.get<PaginatedResponse<Chat>>(`${this.env.apiUrl}/chats/`, {
      params: cleanHttpParams({
        token,
        page,
        page_size: size
      })
    })
  }

  getChatById(chatId: string, token?: string) {
    return this.http.get<Chat>(`${this.env.apiUrl}/chats/${chatId}/`, {
      params: cleanHttpParams({ token })
    })
  }

  updateChat(chatId: string, body: ChatUpdateBody, token?: string) {
    return this.http.patch<Chat>(`${this.env.apiUrl}/chats/${chatId}/`, body, {
      params: cleanHttpParams({ token })
    })
  }

  deleteChat(chatId: string, token?: string) {
    return this.http.delete<string>(`${this.env.apiUrl}/chats/${chatId}/`, {
      params: cleanHttpParams({ token })
    })
  }

  addParticipant(chatId: string, body: AddNewParticipantBody, token?: string) {
    return this.http.post<void>(`${this.env.apiUrl}/chats/${chatId}/participant/`, body, {
      params: cleanHttpParams({ token })
    })
  }

  removeParticipant(chatId: string, userId: string, token?: string) {
    return this.http.delete<string>(`${this.env.apiUrl}/chats/${chatId}/participants/${userId}/`, {
      params: cleanHttpParams({ token })
    })
  }

  sendMessage(chatId: string, body: CreateNewMessageBody, token?: string) {
    return this.http.post<void>(`${this.env.apiUrl}/chats/${chatId}/message/`, body, {
      params: cleanHttpParams({ token })
    })
  }

  getMessages(chatId: string, token?: string) {
    return this.http
      .get<ChatMessage[]>(`${this.env.apiUrl}/chats/${chatId}/messages/`, {
        params: cleanHttpParams({ token })
      })
      .pipe(
        map((messages) => {
          return messages.map((m) => ({
            ...m,
            content: (isJSONString(m.content)
              ? JSON.parse(m.content as string)
              : m.content) as MessageContent[]
          }))
        })
      )
  }

  updateMessage(messageId: string, body: MessageUpdateBody, token?: string) {
    return this.http.patch<ChatMessage>(`${this.env.apiUrl}/chats/messages/${messageId}/`, body, {
      params: cleanHttpParams({ token })
    })
  }

  getChatSummary(chatId: string, token?: string) {
    return this.http.get<ChatSummaryResponse>(`${this.env.apiUrl}/chats/${chatId}/summary/`, {
      params: cleanHttpParams({ token })
    })
  }
}
